import { WorkspaceSettingsPage } from '@/enums/WorkspaceSettingsPage';
import ModernPlanType from '@/pages/subscription/types/ModernPlanType';

/* eslint-disable no-param-reassign */
type Config = {
  anchor?: string;
  format?: string;
  params?: any;
  anchorParams?: any;
};

export const pathWithConfig = (path: string, config?: Config): string => {
  if (!config) {
    return path;
  }

  if (config.format) {
    path += `.${config.format}`;
  }

  if (config.params) {
    const keyValuePairs =
      typeof config.params === 'string'
        ? config.params
        : Object.entries(config.params)
            .filter(([, val]) => Boolean(val))
            .map(([key, val]) => `${key}=${encodeURIComponent(val as string)}`)
            .join('&');

    if (keyValuePairs.length) {
      path += `?${keyValuePairs}`;
    }
  }

  if (config.anchor) {
    path += `#${config.anchor}`;
  }

  if (config.anchorParams) {
    const keyValuePairs =
      typeof config.anchorParams === 'string'
        ? config.anchorParams
        : Object.entries(config.anchorParams)
            .filter(([, val]) => Boolean(val))
            .map(([key, val]) => `${key}=${encodeURIComponent(val as string)}`)
            .join('&');

    if (keyValuePairs.length) {
      path += `?${keyValuePairs}`;
    }
  }

  return path;
};

export const basePathForApp = (appSlug: string): string => {
  return `/app/${appSlug}`;
};

const basePathForOrganization = (slug: string): string => {
  if (!slug) throw new Error('Invalid organization slug.');

  return `/organization/${slug}`;
};

const basePathForAccount = (accountType: string, accountSlug?: string): string => {
  if (!accountType) throw new Error('No account type');

  switch (accountType.toLowerCase()) {
    case 'user':
      return '/me/profile';
    case 'organization':
      if (!accountSlug) throw new Error('Invalid organization slug.');

      return basePathForOrganization(accountSlug);
    default:
      throw new Error('Invalid account type');
  }
};

export const ciOverviewPath = (config?: Config): string => {
  return pathWithConfig('/ci', config);
};

export const buildCachePath = (config?: Config): string => {
  return pathWithConfig('/build-cache', config);
};

export const buildCacheConnectionPath = (workspaceSlug: string, config?: Config): string => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/connections/new`, config);
};

export const buildCacheProjectListPath = (workspaceSlug: string, config?: Config): string => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/connections/projects`, config);
};

export const buildCacheDeletePath = (workspaceSlug: string, config?: Config): string => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/entries`, config);
};

export const buildCacheOperationStatusPath = (workspaceSlug: string, config?: Config): string => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/entries/operation_status`, config);
};

export const outgoingWebhooksPath = (workspaceSlug: string, webhookId?: string) =>
  `/workspaces/${workspaceSlug}/outgoing-webhooks${webhookId ? `/${webhookId}` : ''}`;

export const migrateGitProviderPath = (workspaceSlug: string) =>
  `/workspaces/${workspaceSlug}/settings/migrate_git_provider`;

export const outgoingWebhooksTestPath = (workspaceSlug: string) =>
  `/workspaces/${workspaceSlug}/outgoing-webhooks/test`;

export const releaseManagementStoreCredentialsPath = (workspaceSlug: string, projectId: string): string => {
  return `/release-management/workspaces/${workspaceSlug}/projects/${projectId}/store-credentials`;
};

export const releaseManagementConnectedAppsPagePath = (
  workspaceSlug: string,
  params?: Record<string, string>,
): string => {
  return pathWithConfig(`/release-management/workspaces/${workspaceSlug}/connected-apps`, {
    params,
  });
};

export const releaseManagementConnectedAppPagePath = (
  workspaceSlug: string,
  connectedAppId: string,
  params?: Record<string, string>,
): string => {
  return pathWithConfig(`/release-management/workspaces/${workspaceSlug}/connected-apps/${connectedAppId}`, {
    params,
  });
};

export const releaseManagementConnectedAppPresetsListPagePath = (
  workspaceSlug: string,
  connectedAppId: string,
  params?: Record<string, string>,
): string => {
  return pathWithConfig(`${releaseManagementConnectedAppPagePath(workspaceSlug, connectedAppId)}/presets`, {
    params,
  });
};

export const releaseManagementConnectedAppPresetsPagePath = (
  workspaceSlug: string,
  connectedAppId: string,
  presetsId: string,
): string => {
  return `${releaseManagementConnectedAppPagePath(workspaceSlug, connectedAppId)}/presets/${presetsId}`;
};

export const releaseManagementConnectedAppSettingsPagePath = (
  workspaceSlug: string,
  connectedAppId: string,
  params?: Record<string, string>,
): string => {
  return pathWithConfig(`${releaseManagementConnectedAppPagePath(workspaceSlug, connectedAppId)}/settings`, {
    params,
  });
};

export const releaseManagementConnectedAppCodePushPagePath = (
  workspaceSlug: string,
  connectedAppId: string,
  params?: Record<string, string>,
): string => {
  return pathWithConfig(`${releaseManagementConnectedAppPagePath(workspaceSlug, connectedAppId)}/code-push`, {
    params,
  });
};

export const releaseManagementConnectedAppDeploymentsPagePath = (
  workspaceSlug: string,
  connectedAppId: string,
): string => {
  return `${releaseManagementConnectedAppPagePath(workspaceSlug, connectedAppId)}/code-push/deployments`;
};

export const releaseManagementConnectedAppCodeSigningPagePath = (
  workspaceSlug: string,
  connectedAppId: string,
): string => {
  return `${releaseManagementConnectedAppPagePath(workspaceSlug, connectedAppId)}/code-push/code-signing`;
};

export const releaseManagementConnectedAppIntegrationsPagePath = (
  workspaceSlug: string,
  connectedAppId: string,
  tab?: 'store' | 'feature-flags',
): string => {
  const queryParam = tab ? `?tab=${tab}` : '';

  return `${releaseManagementConnectedAppPagePath(workspaceSlug, connectedAppId)}/integrations${queryParam}`;
};

export const releaseManagementConnectedAppBuildDistributionsPath = (
  workspaceSlug: string,
  connectedAppId: string,
  params?: Record<string, string>,
): string => {
  return pathWithConfig(`${releaseManagementConnectedAppPagePath(workspaceSlug, connectedAppId)}/build-distributions`, {
    params,
  });
};

export const releaseManagementConnectedAppBuildDistributionPath = (
  workspaceSlug: string,
  connectedAppId: string,
  testBuildUid: string,
): string => {
  return `${releaseManagementConnectedAppPagePath(workspaceSlug, connectedAppId)}/build-distributions/${testBuildUid}`;
};

export const releaseManagementConnectedAppTesterGroupPagePath = (
  workspaceSlug: string,
  connectedAppId: string,
  groupId: string,
): string => {
  return `${releaseManagementConnectedAppPagePath(workspaceSlug, connectedAppId)}/tester-groups/${groupId}`;
};

export const releaseManagementConnectedAppInstallableArtifactsPath = (
  workspaceSlug: string,
  connectedAppId: string,
  searchParams?: URLSearchParams,
): string => {
  const queryParam = searchParams ? `?${searchParams}` : '';
  return `${releaseManagementConnectedAppPagePath(workspaceSlug, connectedAppId)}/installable-artifacts${queryParam}`;
};

export const releaseManagementConnectedAppInstallableArtifactPath = (
  workspaceSlug: string,
  connectedAppId: string,
  artifactId: string,
) => {
  return `${releaseManagementConnectedAppInstallableArtifactsPath(workspaceSlug, connectedAppId)}/${artifactId}`;
};

export const releaseManagementConnectedAppInstallableArtifactPrivateInstallUrlPath = (
  workspaceSlug: string,
  connectedAppId: string,
  artifactId: string,
) => {
  return `${releaseManagementConnectedAppInstallableArtifactPath(workspaceSlug, connectedAppId, artifactId)}/private-install-url`;
};

export const releaseManagementCreateReleasePagePath = (workspaceSlug: string, connectedAppId: string): string => {
  return `${releaseManagementConnectedAppPagePath(workspaceSlug, connectedAppId)}/create-new-release`;
};

export const releaseManagementConnectedAppReleasePagePath = (
  workspaceSlug: string,
  connectedAppId: string,
  releaseId: string,
): string => {
  return pathWithConfig(
    `${releaseManagementConnectedAppPagePath(workspaceSlug, connectedAppId)}/releases/${releaseId}`,
  );
};

export const releaseManagementConnectedAppStagePath = (
  workspaceSlug: string,
  connectedAppId: string,
  releaseId: string,
  stageName: string,
) => `${releaseManagementConnectedAppReleasePagePath(workspaceSlug, connectedAppId, releaseId)}/stages/${stageName}`;

export const releaseManagementConnectedAppConfigurationPath = (
  workspaceSlug: string,
  connectedAppId: string,
  releaseId: string,
  tab?: string,
) =>
  pathWithConfig(
    `${releaseManagementConnectedAppReleasePagePath(workspaceSlug, connectedAppId, releaseId)}/stages/configuration`,
    {
      params: tab && { tab },
    },
  );

export const releaseManagementConnectedAppProjectsPath = (workspaceSlug: string) =>
  `${releaseManagementConnectedAppsPagePath(workspaceSlug)}/projects`;

export const prCommentSettingsPath = (projectSlug: string) => `/app/${projectSlug}/pr_comment_settings`;

export const getGoogleServiceCredentialsPath = (workspaceSlug: string) =>
  `/workspaces/${workspaceSlug}/google-service-credentials`;

export const deleteGoogleServiceCredentialPath = (workspaceSlug: string, credentialId: number | string) =>
  `/workspaces/${workspaceSlug}/google-service-credentials/${credentialId}`;

export const appleApiCredentialsPath = (workspaceSlug: string) => `/workspaces/${workspaceSlug}/apple-api-credentials`;

export const deleteAppleApiCredentialsPath = (workspaceSlug: string, keyId: number | string) =>
  `${appleApiCredentialsPath(workspaceSlug)}/${keyId}`;

export const insightsPath = (accountSlug: string, config?: Config): string => {
  return pathWithConfig(`/insights/${accountSlug}/overview`, config);
};

export const insightsExploreBuildsPath = (accountSlug: string, config?: Config): string =>
  pathWithConfig(`/insights/${accountSlug}/explore/builds`, config);

export const getInsightsExploreBuildCachePath = (accountSlug: string, config?: Config): string =>
  pathWithConfig(`/insights/${accountSlug}/explore/build-cache`, config);

export const insightsExploreUtilizationPath = (accountSlug: string, config?: Config): string =>
  pathWithConfig(`/insights/${accountSlug}/explore/utilization`, config);

export const insightsExploreCommandsPath = (accountSlug: string, config?: Config): string =>
  pathWithConfig(`/insights/${accountSlug}/explore/commands`, config);

export const insightsExploreTestsPath = (accountSlug: string, config?: Config): string =>
  pathWithConfig(`/insights/${accountSlug}/explore/tests`, config);

export const addNewAppPath = (projectId?: string): string => {
  return pathWithConfig('/apps/add', projectId ? { params: { project_id: projectId } } : {});
};

export const appSettingsPath = (appSlug: string, subPath?: string, config?: Config): string => {
  let settingsPath = `/app/${appSlug}/settings`;
  settingsPath = subPath ? `${settingsPath}/${subPath}` : settingsPath;
  return pathWithConfig(settingsPath, config);
};

export const appSettingsIntegrationsPagePath = (
  appSlug: string,
  tab?: 'stores' | 'git-provider' | 'webhooks',
): string => {
  return pathWithConfig(appSettingsPath(appSlug, 'integrations'), {
    params: { tab },
  });
};

export const projectSettingsPath = (
  projectId: string,
  subPath?: 'general' | 'integrations' | 'collaboration' | 'test-apple-developer-account-connection',
  config?: Config,
): string => {
  let settingsPath = `/projects/${projectId}/settings`;
  settingsPath = subPath ? `${settingsPath}/${subPath}` : settingsPath;
  return pathWithConfig(settingsPath, config);
};

export const projectSettingsIntegrationsPagePath = (
  projectId: string,
  tab?: 'stores' | 'git-provider' | 'webhooks',
): string => {
  return pathWithConfig(projectSettingsPath(projectId, 'integrations'), {
    params: { tab },
  });
};

export const projectInviteOutsideContributorsPath = (accountSlug: string, projectId: string): string => {
  return `/workspaces/${accountSlug}/projects/${projectId}/invite_outside_contributors`;
};

export const projectAssignRolesPath = (accountSlug: string): string => {
  return `/workspaces/${accountSlug}/access/project_roles`;
};

export const projectAssignGroupRolesPath = (accountSlug: string): string => {
  return `/workspaces/${accountSlug}/access/group_project_roles`;
};

export const connectedAppAssignRolesPath = (accountSlug: string): string => {
  return `/workspaces/${accountSlug}/access/connected_app_roles`;
};

export const billingPath = (accountType: string, accountSlug?: string, config?: Config): string => {
  if (!config) {
    config = {};
  }
  config.anchor = 'billing';

  return pathWithConfig(basePathForAccount(accountType, accountSlug), config);
};

export const editAccountPath = (accountType: string, accountSlug?: string, config?: Config): string => {
  if (!config) {
    config = {};
  }
  config.anchor = 'edit_profile';

  return pathWithConfig(basePathForAccount(accountType, accountSlug), config);
};

export const userCancellationReasonsPath = (config?: Config): string => {
  return pathWithConfig('/me/account_cancellation_reasons', config);
};

export const userSecurityPagePath = (config?: Config): string => {
  return pathWithConfig('/me/account/security', config);
};

export const organizationCancellationReasonsPath = (accountSlug: string, config?: Config): string => {
  return pathWithConfig(`/organizations/${accountSlug}/account_cancellation_reasons`, config);
};

export const sendAccountDeleteReasonPath = (accountType: string, accountSlug?: string, config?: Config): string => {
  return pathWithConfig(`${basePathForAccount(accountType, accountSlug)}/send_account_delete_reason`, config);
};

export const ownedAppsPath = (accountType: string, accountSlug?: string, config?: Config): string => {
  if (!config) {
    config = {
      format: 'json',
    };
  }

  if (accountType === 'User') {
    config.params = {
      user_role: 'owner',
      only_direct: true,
    };
  } else {
    config.params = {
      organization_slug: accountSlug,
    };
  }

  return pathWithConfig('/apps', config);
};

export const ownedAppsListPath = (
  accountType: string,
  accountSlug?: string,
  limit?: number,
  config?: Config,
): string => {
  if (!config) {
    config = {
      format: 'json',
    };
  }

  const params: Record<string, unknown> = {};
  if (accountType === 'Organization') {
    params.organization_slug = accountSlug;
  }
  if (limit !== undefined) {
    params.limit = limit;
  }
  if (Object.keys(params).length > 0) {
    config.params = params;
  }

  return pathWithConfig('/me/owned_apps', config);
};

export const ownedOrganizationsPath = (config?: Config): string => {
  if (!config) {
    config = {
      format: 'json',
    };
  }

  config.params = {
    only_single_confirmed_owner: true,
  };

  return pathWithConfig('/me/profile/organizations', config);
};

export const accountDeletePath = (accountType: string, accountSlug?: string, config?: Config): string => {
  if (!config) {
    config = {
      format: 'json',
    };
  }

  if (accountType === 'User') {
    return pathWithConfig('/users', config);
  }

  return pathWithConfig(basePathForAccount(accountType, accountSlug), config);
};

export const newCreditSubscriptionPath = (organizationSlug: string, plan?: ModernPlanType): string => {
  return `/organization/${organizationSlug}/credit_subscription/new${plan ? `?plan=${plan}` : ''}`;
};

export const editCreditSubscriptionPath = (organizationSlug: string, plan: ModernPlanType): string => {
  return `/organization/${organizationSlug}/credit_subscription/edit?plan=${plan}`;
};

export const planSelectorPagePath = (organizationSlug: string): string => {
  return `/organization/${organizationSlug}/credit_subscription/plan_selector_page`;
};

export const addCreditCardPath = (organizationSlug: string): string => {
  return `/organization/${organizationSlug}/credit_subscription/add_credit_card`;
};

export const addOnCheckoutPagePath = (organizationSlug: string): string => {
  return `/organization/${organizationSlug}/credit_subscription/add_ons_page`;
};

export const topUpExtraBuildCapacityPath = (organizationSlug: string): string => {
  return `/organization/${organizationSlug}/credit_subscription/top_up_extra_build_capacity`;
};

export const extraBuildCapacityOptionsPath = (organizationSlug: string, planId?: number): string => {
  return pathWithConfig(`/organization/${organizationSlug}/credit_subscription/extra_build_capacity_options`, {
    params: {
      plan_id: planId,
    },
  });
};

export const workspaceMembersPath = (workspaceSlug: string): string => {
  return `/workspaces/${workspaceSlug}/members`;
};

export const workspaceSettingsConnectedAppsPath = (workspaceSlug: string): string => {
  return `/workspaces/${workspaceSlug}/settings/connected_apps`;
};

export const workspaceSettingsAddMemberPath = (workspaceSlug: string): string => {
  return `/workspaces/${workspaceSlug}/settings/add-member`;
};

export const workspaceSettingsAddGroupPath = (workspaceSlug: string): string => {
  return `/workspaces/${workspaceSlug}/settings/create-group`;
};

export const workspaceSettingsRoleEditorPath = (workspaceSlug: string, roleId?: string): string => {
  const path = `/workspaces/${workspaceSlug}/settings/role-editor`;
  return roleId ? `${path}?role_id=${roleId}` : path;
};

export const APP_STORE_INTEGRATION = 'app-store';
export const GOOGLE_PLAY_INTEGRATION = 'google-play';

export const workspaceSettingsIntegrationsPath = (organizationSlug: string, integration?: string): string => {
  return pathWithConfig(`/workspaces/${organizationSlug}/settings/integrations`, {
    params: {
      integration,
    },
  });
};

export const workflowEditorPath = (appSlug: string, workflowId?: string | null, pipelineId?: string | null): string => {
  let anchor = '!/workflows';
  if (pipelineId) {
    anchor = '!/pipelines';
  }

  let anchorParams = null;
  if (workflowId) {
    anchorParams = { workflow_id: workflowId };
  } else if (pipelineId) {
    anchorParams = { pipeline: pipelineId };
  }

  return pathWithConfig(`/app/${appSlug}/workflow_editor`, {
    anchor,
    anchorParams,
  });
};

export const workflowEditorPathWithConfig = (appSlug: string, config?: Config): string => {
  return pathWithConfig(`/app/${appSlug}/workflow_editor`, {
    anchor: '!/workflows',
    params: config?.params,
    format: config?.format,
    anchorParams: config?.anchorParams,
  });
};

export const workflowEditorStackPath = (appSlug: string): string => {
  return pathWithConfig(`/app/${appSlug}/workflow_editor`, { anchor: '!/stack' });
};

export const workflowEditorTriggersPath = (appSlug: string, config?: Config): string => {
  return pathWithConfig(`/app/${appSlug}/workflow_editor`, {
    anchor: '!/triggers',
    params: config?.params,
    format: config?.format,
    anchorParams: config?.anchorParams,
  });
};

export const disabledPath = (appSlug: string): string => {
  return `/app/${appSlug}/disabled.json`;
};

export const supportAccessPath = (repositorySlug: string): string => {
  return `/app/${repositorySlug}/support_access.json`;
};

export const startBuildPath = (appSlug: string): string => {
  return `/app/${appSlug}/build/start.json`;
};

export const triggerNowPath = (scheduleId: string): string => {
  return `/api/schedule/${scheduleId}/trigger.json`;
};

export const abortBuildPath = (buildSlug: string): string => {
  return `/build/${buildSlug}/abort.json`;
};

export const rebuildBuildPath = (buildSlug: string): string => {
  return `/build/${buildSlug}/rebuild.json`;
};

export const abortPipelinePath = (appSlug: string, pipelineSlug: string): string => {
  return `/app/${appSlug}/pipelines/${pipelineSlug}/abort`;
};

export const appBitriseYMLPath = (appSlug: string): string => {
  return `/api/app/${appSlug}/config.yml`;
};

export const restoreAppBitriseYMLPath = (appSlug: string): string => {
  return `/api/app/${appSlug}/config.json`;
};

export const hasBuildBitriseYmlPath = (buildSlug: string) => `/build/${buildSlug}/has_bitrise_yml`;

export const hasPipelineBitriseYmlPath = (appSlug: string, pipelineId: string, attemptId: string) =>
  `/app/${appSlug}/pipelines/${pipelineId}/attempts/${attemptId}/has_bitrise_yml`;

export const buildBitriseYMLPath = (buildSlug: string): string => {
  return `/build/${buildSlug}/bitrise_yml`;
};

export const pipelineBitriseYMLPath = (appSlug: string, pipelineId: string, attemptId: string): string => {
  return `/app/${appSlug}/pipelines/${pipelineId}/attempts/${attemptId}/bitrise_yml`;
};

export const deleteBuildBitriseYMLPath = (buildSlug: string): string => {
  return buildBitriseYMLPath(buildSlug);
};

export const deletePipelineBitriseYMLPath = (appSlug: string, pipelineId: string): string => {
  return `/app/${appSlug}/pipelines/${pipelineId}/bitrise_yml`;
};

export const installableArtifactsPath = (appSlug: string, searchParams?: URLSearchParams) => {
  const queryParam = searchParams ? `?${searchParams}` : '';
  return `${basePathForApp(appSlug)}/installable-artifacts${queryParam}`;
};

export const installableArtifactPath = (appSlug: string, artifactSlug: string) => {
  return `${basePathForApp(appSlug)}/installable-artifacts/${artifactSlug}`;
};

export const installableArtifactPrivateInstallUrlPath = (appSlug: string, artifactSlug: string) => {
  return `${basePathForApp(appSlug)}/installable-artifacts/${artifactSlug}/private-install-url`;
};

export const togglePublicInstallPagePath = (appSlug: string, artifactSlug: string): string => {
  return `${basePathForApp(appSlug)}/installable-artifacts/${artifactSlug}/public-install-page`;
};

export const buildLogPath = (buildSlug: string): string => {
  return `/build/${buildSlug}/logs`;
};

export const buildDetailsPagePath = (appSlug: string | undefined, buildSlug: string, tab?: string): string => {
  const basePath = appSlug ? `/app/${appSlug}/build/${buildSlug}` : `/build/${buildSlug}`;
  return pathWithConfig(basePath, {
    params: tab ? { tab } : null,
  });
};

export const pipelineDetailsPagePath = (appSlug: string | undefined, pipelineId: string, tab?: string): string => {
  if (!appSlug || !pipelineId) {
    return '#';
  }

  return pathWithConfig(`/app/${appSlug}/pipelines/${pipelineId}`, {
    params: tab ? { tab } : null,
  });
};

export const pipelineAttemptDetailsPagePath = (
  appSlug: string | undefined,
  pipelineId: string,
  attemptId?: string,
  tab?: string,
): string => {
  if (!appSlug || !pipelineId) {
    return '#';
  }

  if (!attemptId) {
    return pipelineDetailsPagePath(appSlug, pipelineId);
  }

  return pathWithConfig(`/app/${appSlug}/pipelines/${pipelineId}/attempts/${attemptId}`, {
    params: tab ? { tab } : null,
  });
};

export const pipelineTestResultsPath = (appSlug: string, pipelineId: string, attemptId: string): string => {
  if (!appSlug || !pipelineId || !attemptId) {
    return '#';
  }

  return `${pipelineAttemptDetailsPagePath(appSlug, pipelineId, attemptId)}/test-results`;
};

export const getArtifactPathForBuild = (buildSlug?: string): string => {
  if (!buildSlug) {
    return '';
  }
  return `/build/${buildSlug}/artifacts.json`;
};

export const getArtifactPathForPipeline = (appSlug?: string, pipelineSlug?: string, attemptId?: string): string => {
  if (!appSlug || !pipelineSlug) {
    return '';
  }
  return attemptId
    ? `/app/${appSlug}/pipelines/${pipelineSlug}/attempts/${attemptId}/artifacts.json`
    : `/app/${appSlug}/pipelines/${pipelineSlug}/artifacts.json`;
};

export const insightsTokenPath = (config?: Config): string => {
  return pathWithConfig('/insights/insights_token', config);
};

export const appDeletePath = (appSlug: string) => {
  return `/app/${appSlug}/destroy.json`;
};

export const signUpPath = (): string => {
  return '/users/sign_up';
};

export const logInPath = (): string => {
  return '/users/sign_in';
};

export const appPath = (appSlug: string): string => {
  return `/api/app/${appSlug}`;
};

export const appWorkflowsPath = (appSlug: string, branch?: string | null): string => {
  const path = `/api/app/${appSlug}/workflows`;
  return branch ? `${path}?branch=${encodeURIComponent(branch)}` : path;
};

export const appPipelinesPath = (appSlug: string): string => {
  return `/app/${appSlug}/pipelines`;
};

export const appleIdConnectPath = () => '/apple-id-connection/connect';

export const appleIdSelectDevicePath = () => '/apple-id-connection/select-device';

export const appleIdVerifyCodePath = () => '/apple-id-connection/verify-code';

export const projectTypePath = (appSlug: string): string => {
  return `/app/${appSlug}/project_type`;
};

export const transferOwnershipPath = (appSlug: string): string => {
  return `/app/${appSlug}/team/transfer_ownership`;
};

export const transferOwnershipTargetAccounts = (appSlug: string): string => {
  return `/app/${appSlug}/transfer-ownership`;
};

export const accountMetadataPath = (): string => '/me/profile/metadata.json';

export const appAvatarPath = (appSlug: string): string => {
  return `/app/${appSlug}/avatar_url`;
};

export const appAvatarEndedPath = (appSlug: string): string => {
  return `${appAvatarPath(appSlug)}/upload_ended`;
};

export const projectPotentialMembersPath = (projectId: string) => {
  return `/projects/${projectId}/members/potential_members`;
};

export const addProjectContributorPath = (projectId: string) => {
  return `/projects/${projectId}/members/new`;
};

export const newProjectGroupPath = (projectId: string) => {
  return `/projects/${projectId}/groups/new`;
};

export const removeProjectGroupPath = ({ projectId, groupSlug }: { projectId: string; groupSlug: string }) => {
  return `/projects/${projectId}/groups/${groupSlug}`;
};

export const getTestDevicesPath = (appSlug: string) => {
  return `/app/${appSlug}/settings/team-test-devices.json`;
};

export const projectMemberLeavePath = (id: string) => {
  return `/projects/${id}/members/leave`;
};

export const toggleRollingBuildsPath = (appSlug: string): string => `/app/${appSlug}/addon/addons-rolling-build`;

export const rollingBuildsTypesPath = (appSlug: string): string => `/app/${appSlug}/rolling_builds_toggle`;

export const organizationAvatarPath = (organizationSlug: string) => {
  return `/organization/${organizationSlug}/avatar_url`;
};

export const organizationAvatarEndedPath = (organizationSlug: string) => {
  return `/organization/${organizationSlug}/avatar_url/upload_ended`;
};

export const organizationDefaultAvatar = (organizationSlug: string) => {
  return `/organization/${organizationSlug}/default_avatar_url`;
};

export const profileAvatarPath = () => {
  return `/me/profile/avatar_url`;
};

export const profileAvatarEndedPath = () => {
  return `/me/profile/avatar_url/upload_ended`;
};

export const selectiveBuildsPathsToBuild = (appSlug: string): string => `/app/${appSlug}/paths_to_build`;

export const checkGHESRepositoryConnectionHealthPath = (appSlug: string) =>
  `/app/${appSlug}/github_app_installation/connection_health`;

export const gitHubEnterpriseConnectionPath = (workspaceSlug: string) => {
  return `/organization/${workspaceSlug}/github_app`;
};

export const gitHubEnterpriseConnectionHealthPath = (workspaceSlug: string) => {
  return `/organization/${workspaceSlug}/github_app/connection_health`;
};

export const selectiveBuildsEnabledPath = (appSlug: string): string => `/app/${appSlug}/selective_builds_enabled`;

export const manualApprovalEnabledPath = (appSlug: string): string => `/app/${appSlug}/manual_approval_enabled`;

export const buildCacheProjectFilterValuesPath = (workspaceSlug: string, buildTool: string, config?: Config) => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/invocations/${buildTool}/projects.json`, config);
};

export const buildCacheCiProviderFilterValuesPath = (workspaceSlug: string, buildTool: string, config?: Config) => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/invocations/${buildTool}/ci-providers.json`, config);
};

export const buildCacheWorkflowNameFilterValuesPath = (workspaceSlug: string, buildTool: string, config?: Config) => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/invocations/${buildTool}/workflows.json`, config);
};

export const buildCacheCommandFilterValuesPath = (workspaceSlug: string, buildTool: string, config?: Config) => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/invocations/${buildTool}/commands.json`, config);
};

export const buildCacheUsernameFilterValuesPath = (workspaceSlug: string, buildTool: string, config?: Config) => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/invocations/${buildTool}/usernames.json`, config);
};

export const subscribeToBuildToolWaitlistPath = (workspaceSlug: string, config?: Config) => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/subscribe-to-build-tool-waitlist.json`, config);
};

export const buildCacheBenchmarkDataPath = (workspaceSlug: string, config?: Config) => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/time-saved-stats`, config);
};

export const keyValueCacheItemsPath = (appSlug: string, config?: Config) => {
  return pathWithConfig(`/app/${appSlug}/key-value-cache/items`, config);
};

export const keyValueCacheItemsDeletePath = (appSlug: string, config?: Config) => {
  return pathWithConfig(`/app/${appSlug}/key-value-cache/items/delete`, config);
};

const keyValueCacheItemsApiPath = (appSlug: string) => `/v0.1/apps/${appSlug}/cache-items`;

const keyValueCacheItemResourceApiPath = (appSlug: string, cacheId: string) =>
  `${keyValueCacheItemsApiPath(appSlug)}/${cacheId}`;

export const keyValueCacheItemDownloadApiPath = (appSlug: string, cacheId: string) =>
  `${keyValueCacheItemResourceApiPath(appSlug, cacheId)}/download`;

export const nextBuildNumberPath = (appSlug: string) => `/app/${appSlug}/next_build_number`;

export const regenerateBuildTokenPath = (appSlug: string) => `/app/${appSlug}/regenerate_build_trigger_token`;

export const gitHubChecksEnabledPath = (appSlug: string) => `/app/${appSlug}/github_checks_enabled`;

export const updateEmailNotificationsPath = (appSlug: string) => `/app/${appSlug}/update_email_notifications.json`;

export const hasRegisteredWebhookPath = (appSlug: string) => `/app/${appSlug}/has-registered-webhook`;

export const registerWebhookPath = (appSlug: string) => `/app/${appSlug}/register-webhook`;

export const gitHubAppWebhooksPath = (appSlug: string) => `/app/${appSlug}/settings/integrations/github-app/webhooks`;

export const generateSshKeysPath = () => `/utilities/ssh_keygen.json`;

export const registerSshKeysPath = (appSlug: string) => `/app/${appSlug}/register-ssh-keys.json`;

export const removeSshKeysPath = (appSlug: string) => `/app/${appSlug}/remove-ssh-keys`;

export const saveGitHttpCredsPath = (appSlug: string) => `/app/${appSlug}/git-http-credentials.json`;

export const removeGitHttpCredsPath = (appSlug: string) => `/app/${appSlug}/git-http-credentials.json`;

export const appWatchPath = (appSlug: string) => `/app/${appSlug}/watch`;

export const renewUserAuthTokenPath = () => `/me/profile/security/create_website_user_auth_token`;

export const creditSurveyPath = (workspaceSlug: string) => `/workspaces/${workspaceSlug}/credit_survey`;

export const buildCacheSubscriptionPath = (workspaceSlug: string, config?: Config) => {
  return pathWithConfig(`/workspaces/${workspaceSlug}/build_cache_subscription`, config);
};

export const activateBuildCacheSubscriptionEventPath = (workspaceSlug: string) =>
  `/workspaces/${workspaceSlug}/build_cache_flows/activation.json`;

export const buildCacheRequestSupportPath = (workspaceSlug: string) =>
  `/workspaces/${workspaceSlug}/build_cache_flows/support_request.json`;

export const buildCacheTabOfBuildPath = (workspaceSlug: string, buildSlug: string) =>
  `/workspaces/${workspaceSlug}/build_cache_flows/builds/${buildSlug}/build_cache_tab`;

export const compareInvocationsPath = (workspaceSlug: string, buildTool: string, config?: Config) => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/invocations/${buildTool}/compare`, config);
};

export const compareInvocationsTaskInputsDataPath = (workspaceSlug: string, buildTool: string, config?: Config) => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/invocations/${buildTool}/compare/task-inputs`, config);
};

export const compareInvocationTaskInputFilesPath = (
  workspaceSlug: string,
  buildTool: string,
  taskId: string,
  config?: Config,
) => {
  return pathWithConfig(
    `/build-cache/${workspaceSlug}/invocations/${buildTool}/compare/task-inputs/${encodeURIComponent(taskId)}/input-files`,
    config,
  );
};

export const compareInvocationTaskOutputFilesPath = (
  workspaceSlug: string,
  buildTool: string,
  taskId: string,
  config?: Config,
) => {
  return pathWithConfig(
    `/build-cache/${workspaceSlug}/invocations/${buildTool}/compare/task-inputs/${encodeURIComponent(taskId)}/output-files`,
    config,
  );
};

export const compareInvocationsLoadMoreTaskInputFilesPath = (
  workspaceSlug: string,
  buildTool: string,
  taskId: string,
  config?: Config,
) => {
  return pathWithConfig(
    `/build-cache/${workspaceSlug}/invocations/${buildTool}/compare/task-inputs/${encodeURIComponent(taskId)}/input-files/more`,
    config,
  );
};

export const compareInvocationsLoadMoreTaskOutputFilesPath = (
  workspaceSlug: string,
  buildTool: string,
  taskId: string,
  config?: Config,
) => {
  return pathWithConfig(
    `/build-cache/${workspaceSlug}/invocations/${buildTool}/compare/task-inputs/${encodeURIComponent(taskId)}/output-files/more`,
    config,
  );
};

export const compareInvocationsFeedbackPath = (workspaceSlug: string, buildTool: string, config?: Config) => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/invocations/${buildTool}/compare_feedback`, config);
};

export const aiSummaryForComparedInvocationsPath = (workspaceSlug: string, buildTool: string, config?: Config) => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/invocations/${buildTool}/compare/summary`, config);
};

export const invocationStepExecutionPath = (
  workspaceSlug: string,
  buildTool: string,
  invocationId: string,
  stepExecutionId: string,
) => `/build-cache/${workspaceSlug}/invocations/${buildTool}/${invocationId}/step-executions/${stepExecutionId}`;

export const invocationTaskInputsDataPath = (
  workspaceSlug: string,
  buildTool: string,
  invocationId: string,
  config?: Config,
) => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/invocations/${buildTool}/${invocationId}/task-inputs`, config);
};

export const invocationTaskInputFilesPath = (
  workspaceSlug: string,
  buildTool: string,
  invocationId: string,
  taskId: string,
  config?: Config,
) => {
  return pathWithConfig(
    `/build-cache/${workspaceSlug}/invocations/${buildTool}/${invocationId}/task-inputs/${encodeURIComponent(taskId)}/input-files`,
    config,
  );
};

export const invocationTaskOutputFilesPath = (
  workspaceSlug: string,
  buildTool: string,
  invocationId: string,
  taskId: string,
  config?: Config,
) => {
  return pathWithConfig(
    `/build-cache/${workspaceSlug}/invocations/${buildTool}/${invocationId}/task-inputs/${encodeURIComponent(taskId)}/output-files`,
    config,
  );
};

export const invocationLoadMoreTaskInputFilesPath = (
  workspaceSlug: string,
  buildTool: string,
  invocationId: string,
  taskId: string,
  config?: Config,
) => {
  return pathWithConfig(
    `/build-cache/${workspaceSlug}/invocations/${buildTool}/${invocationId}/task-inputs/${encodeURIComponent(taskId)}/input-files/more`,
    config,
  );
};

export const invocationLoadMoreTaskOutputFilesPath = (
  workspaceSlug: string,
  buildTool: string,
  invocationId: string,
  taskId: string,
  config?: Config,
) => {
  return pathWithConfig(
    `/build-cache/${workspaceSlug}/invocations/${buildTool}/${invocationId}/task-inputs/${encodeURIComponent(taskId)}/output-files/more`,
    config,
  );
};

export const buildToolInvocationTaskListPath = (
  workspaceSlug: string,
  invocationId: string,
  config: Omit<Config, 'format'> & { format: 'json' | 'csv' },
) => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/invocations/gradle/${invocationId}/tasks`, config);
};

export const buildToolInvocationTargetListPath = (
  workspaceSlug: string,
  invocationId: string,
  config: Omit<Config, 'format'> & { format: 'json' | 'csv' },
) => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/invocations/bazel/${invocationId}/targets`, config);
};

export const buildCacheGradleTestDistributionPath = (workspaceSlug: string, config?: Config) => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/gradle_test_distribution`, config);
};

export const buildCacheBazelRemoteExecutionPath = (workspaceSlug: string, config?: Config) => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/bazel-remote-execution`, config);
};

export const buildCacheSettingsPath = (workspaceSlug: string, config?: Config) => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/settings`, config);
};

export const buildCacheOverviewPath = (workspaceSlug: string, config?: Config) => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/overview`, config);
};

export const buildCacheInvocationListPath = (workspaceSlug: string, config?: Config) => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/invocations`, config);
};

export const buildToolInvocationPath = (
  workspaceSlug: string,
  invocationId: string,
  buildTool: string,
  config?: Config,
): string => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/invocations/${buildTool}/${invocationId}`, config);
};

export const buildToolInvocationCommandProfilePath = (
  workspaceSlug: string,
  invocationId: string,
  config?: Config,
): string => {
  return pathWithConfig(`/build-cache/${workspaceSlug}/invocations/bazel/${invocationId}/command-profile`, config);
};

export const buildToolChildInvocationsPath = (workspaceSlug: string, invocationId: string, config?: Config): string => {
  return pathWithConfig(
    `/build-cache/${workspaceSlug}/invocations/react-native/${invocationId}/child-invocations`,
    config,
  );
};

export const buildToolSiblingInvocationsPath = (
  workspaceSlug: string,
  buildTool: string,
  invocationId: string,
  config?: Config,
): string => {
  return pathWithConfig(
    `/build-cache/${workspaceSlug}/invocations/${buildTool}/${invocationId}/sibling-invocations`,
    config,
  );
};

export const buildTestResultsPath = (buildSlug: string) => `/build/${buildSlug}/test_results`;

export const buildCacheCacheInfo = (buildSlug: string) => `/build/${buildSlug}/build_cache/cache_info`;

const workspaceSettingsRootPath = (workspaceSlug: string) => `/workspaces/${workspaceSlug}/settings`;

export const workspaceSettingsPath = (workspaceSlug: string, page?: WorkspaceSettingsPage) => {
  let path = workspaceSettingsRootPath(workspaceSlug);
  if (page) path += `/${page}`;
  return path;
};

export const workspaceSettingsBillingReleaseManagementPlanPath = (workspaceSlug: string) =>
  `/workspaces/${workspaceSlug}/settings/billing/release-management-plan`;

export const getWorkspaceTeamPath = (workspaceSlug: string, config?: Config): string => {
  return pathWithConfig(`/workspaces/${workspaceSlug}/settings/team`, config);
};

export const getWorkspaceBillingPath = (workspaceSlug: string, config?: Config): string => {
  return pathWithConfig(`/workspaces/${workspaceSlug}/settings/billing`, config);
};

export const workspaceSecurityPath = (workspaceSlug: string, config?: Config): string => {
  return pathWithConfig(`/workspaces/${workspaceSlug}/settings/security`, config);
};

export const customizeBuildCachePlanPagePath = (workspaceSlug: string, config?: Config): string => {
  return pathWithConfig(
    `/organization/${workspaceSlug}/credit_subscription/add_on_subscriptions/build-cache-pro/customize_build_cache_plan`,
    config,
  );
};

export const customizeCodePushPlanPagePath = (workspaceSlug: string, config?: Config): string => {
  return pathWithConfig(
    `/organization/${workspaceSlug}/credit_subscription/add_on_subscriptions/codepush-pro/customize_codepush_plan`,
    config,
  );
};

export const buildCachePlanSelectorPagePath = (workspaceSlug: string, config?: Config): string => {
  return pathWithConfig(
    `/organization/${workspaceSlug}/credit_subscription/add_on_subscriptions/build-cache-pro`,
    config,
  );
};

export const codePushPlanSelectorPagePath = (workspaceSlug: string, config?: Config): string => {
  return pathWithConfig(`/organization/${workspaceSlug}/credit_subscription/add_on_subscriptions/codepush-pro`, config);
};

export const remoteDevEnvironmentsPlanSelectorPagePath = (workspaceSlug: string, config?: Config): string => {
  return pathWithConfig(
    `/organization/${workspaceSlug}/credit_subscription/add_on_subscriptions/remote-development-environments`,
    config,
  );
};

export const remoteDevEnvironmentsCustomizePlanPagePath = (
  workspaceSlug: string,
  plan: 'basic' | 'pro',
  config?: Config,
): string => {
  return pathWithConfig(
    `/organization/${workspaceSlug}/credit_subscription/add_on_subscriptions/remote-development-environments-${plan}/customize_remote_dev_environments_plan`,
    config,
  );
};

export const buildHubPlanSelectorPagePath = (workspaceSlug: string, config?: Config): string => {
  return pathWithConfig(`/organization/${workspaceSlug}/credit_subscription/add_on_subscriptions/build-hub`, config);
};

export const buildHubCustomizePlanPagePath = (
  workspaceSlug: string,
  plan: 'basic' | 'pro',
  config?: Config,
): string => {
  return pathWithConfig(
    `/organization/${workspaceSlug}/credit_subscription/add_on_subscriptions/build-hub-${plan}/customize_build_hub_plan`,
    config,
  );
};

export const getWorkspaceUpgradeInsightsPath = (workspaceSlug: string, config?: Config): string => {
  return pathWithConfig(`/organization/${workspaceSlug}/credit_subscription/add_ons_page`, config);
};

export const getTimeBasedTrialStats = (workspaceSlug: string, config?: Config): string => {
  return pathWithConfig(`/api/organizations/${workspaceSlug}/trial_stats`, config);
};

export const getExtendLimitPath = (workspaceSlug: string): string => {
  return `/api/organizations/${workspaceSlug}/extend_limits`;
};

export const scheduleBuildPath = (appSlug: string, config?: Config): string => {
  return pathWithConfig(`/api/schedule/${appSlug}.json`, config);
};

export const getScheduledNextRunPath = (appSlug: string, config?: Config): string => {
  return pathWithConfig(`/api/schedule/${appSlug}/next_run.json`, config);
};

export const scheduledBuildPath = (scheduleBuildId: string, config?: Config): string => {
  return pathWithConfig(`/api/schedule/${scheduleBuildId}.json`, config);
};

export const exceededLimitsPath = (workspaceSlug: string, config?: Config): string => {
  return pathWithConfig(`/workspaces/${workspaceSlug}/limits-exceeding-downgrade`, config);
};

export const createFileStoragePath = (appSlug: string): string => {
  return `/api/app/${appSlug}/project_file_storage_document/create.json`;
};

export const uploadEndedFileStoragePath = (fileId: string | number): string => {
  return `/project_file_storage_document/${fileId}/upload_ended.json`;
};

export const createCertificatePath = (appSlug: string): string => {
  return `/api/app/${appSlug}/build_certificate/create.json`;
};

export const uploadEndedCertificatePath = (id: string | number): string => {
  return `/build_certificate/${id}/upload_ended.json`;
};

export const previewCertificatePath = (): string => {
  return '/build_certificate/preview';
};

export const passwordCertificatePath = (id: string | number): string => {
  return `/build_certificate/${id}/password.json`;
};

export const detailsCertificatePath = (id: string | number): string => {
  return `/build_certificate/details/${id}`;
};

export const certificatesPath = (appSlug: string): string => {
  return `/api/app/${appSlug}/build_certificate/show.json`;
};

export const certificatesProtect = (fileId: string | number): string => {
  return `/build_certificate/${fileId}/protect.json`;
};

export const certificatesDeletePath = (appSlug: string, fileId?: string | number): string => {
  return `/api/app/${appSlug}/build_certificate/${fileId ? `${fileId}/` : ''}delete.json`;
};

export const updateCertificateExposedPath = (fileId: string | number): string => {
  return `/build_certificate/${fileId}/update_is_expose.json`;
};

export const genericFileStoragePath = (appSlug: string): string => {
  return `/api/app/${appSlug}/project_file_storage_document/show.json`;
};

export const genericFileProtect = (fileId: string | number): string => {
  return `/project_file_storage_document/${fileId}/protect.json`;
};

export const genericFileDelete = (appSlug: string, fileId?: 'all' | number): string => {
  return `/api/app/${appSlug}/project_file_storage_document/${
    fileId === 'all' ? 'all_android_keystore_' : `${fileId}/`
  }delete.json`;
};

export const createProvisioningProfilePath = (appSlug: string): string => {
  return `/api/app/${appSlug}/prov_profile_document/create.json`;
};

export const getProvisioningProfilePath = (appSlug: string): string => {
  return `/api/app/${appSlug}/prov_profile_document/show.json`;
};

export const protectProvisioningProfilePath = (id: string | number): string => {
  return `/prov_profile_document/${id}/protect.json`;
};

export const deleteProvisioningProfilePath = (appSlug: string, fileId?: string | number): string => {
  return `/api/app/${appSlug}/prov_profile_document/${fileId ? `${fileId}/` : ''}delete.json`;
};

export const updateProfileExposedPath = (fileId: string | number): string => {
  return `/prov_profile_document/${fileId}/update_is_expose.json`;
};

export const uploadEndedProvisioningProfilePath = (id: string | number): string => {
  return `/prov_profile_document/${id}/upload_ended.json`;
};

export const previewProvisioningProfilePath = (): string => {
  return '/prov_profile_document/preview';
};

export const previewKeystorePath = (): string => {
  return '/key_store/preview';
};

export const detailsProvisioningProfilePath = (id: string | number): string => {
  return `/prov_profile_document/details/${id}`;
};

export const exposeKeystoreMetaPath = (id: string | number): string => {
  return `/project_file_storage_document/${id}/exposed_meta.json`;
};

export const accountAppleConnectionPath = (): string => '/me/account/apple-id';

export const accountFeatureFlagsPath = () => '/me/account/feature-flags';

export const updateFileExposedPath = (fileId: string | number): string => {
  return `/project_file_storage_document/${fileId}/update_is_expose.json`;
};

export const getBuildHtmlReportsPath = (buildSlug: string) => {
  return `/build/${buildSlug}/html-reports`;
};

export const getPipelineHtmlReportsPath = (appSlug: string, pipelineSlug: string, attemptId: string) => {
  return `/app/${appSlug}/pipelines/${pipelineSlug}/attempts/${attemptId}/html-reports`;
};

export const gitHubAppInstallationRedirectPath = (params: URLSearchParams) => {
  return `/github_app_installation/redirect?${params.toString()}`;
};

export const gitHubAppInstallationConnectPath = (params: URLSearchParams) => {
  return `/github_app_installation/connect?${params.toString()}`;
};

export const gitHubAppInstallationConnectAccountPath = (workspaceSlug: string, params: URLSearchParams) => {
  return `/workspaces/${workspaceSlug}/github_account_ownerships?${params.toString()}`;
};

export const deleteGitHubAccountPath = (workspaceSlug: string, gitHubAccountId: string) => {
  return `/organization/${workspaceSlug}/github_accounts/${gitHubAccountId}`;
};

export const getAIFixerResultPath = (build_slug: string) => {
  return `/api/builds/${build_slug}/ai_fixer_result`;
};

export const postRunAIFixerAgentPath = (appSlug: string) => {
  return `/apps/${appSlug}/agent_runs`;
};

export const postMigrateToGitHubAppIntegrationPath = (appSlug: string) => {
  return `/app/${appSlug}/migrate_to_github_app_integration`;
};

export const postMigrateToGitHubAppTokenAuthorizationPath = (appSlug: string) => {
  return `/app/${appSlug}/migrate_ghes_to_github_app_token_authorization`;
};

export const postRevertGitHubAppIntegrationPath = (appSlug: string) => {
  return `/app/${appSlug}/revert_github_app_integration`;
};

export const getCustomConfigPath = (appSlug: string) => {
  return `/app/${appSlug}/custom_config.json`;
};

export const getStacksAndMachinesPath = (appSlug: string) => {
  return `/app/${appSlug}/stacks_and_machines`;
};

export const getMergedStacksPath = (workspaceSlug: string) => {
  return `/workspaces/${workspaceSlug}/merged_stacks`;
};

export const postProjectConfigPath = (appSlug: string) => {
  return `/app/${appSlug}/finish.json`;
};

export const getGitHubAppLinkedRepositoriesPath = (appSlug: string) => {
  return `/app/${appSlug}/settings/integrations/github-app/linkable-repositories`;
};

export const putGitHubAppLinkedRepositoriesPath = (appSlug: string) => {
  return `/app/${appSlug}/settings/integrations/github-app/linked-repositories`;
};

export const putGitHubAppPermissionsPath = (appSlug: string) => {
  return `/app/${appSlug}/settings/integrations/github-app/permissions`;
};

export const licensePoolsPath = (slug: string) => {
  return `/organizations/${slug}/license_pools`;
};

export const projectBuildList = (appSlug: string): string => {
  return basePathForApp(appSlug);
};

export const getStartBuildDialogDataPath = (appSlug: string, config?: Config) => {
  return pathWithConfig(`/app/${appSlug}/start_build_dialog_data`, config);
};

export const getProjectsPath = (workspaceSlug: string) => `/workspaces/${workspaceSlug}/projects`;

export const getProjectPath = (workspaceSlug: string, projectId: string) =>
  `${getProjectsPath(workspaceSlug)}/${projectId}`;

export const projectSupportAccessPath = (workspaceSlug: string, projectId: string) =>
  `${getProjectsPath(workspaceSlug)}/${projectId}/support_access`;

export const slackIntegrationPath = (workspaceSlug: string): string => {
  return `/workspaces/${workspaceSlug}/slack_integrations`;
};

export const activePipelineablesPath = (appSlug: string) => {
  return `/v0.1/apps/${appSlug}/pipelines/active-pipelineables`;
};

export const projectTestQuarantinePage = (projectSlug: string) => {
  return `/app/${projectSlug}/test_quarantine`;
};

export const projectInvocationsPath = (projectSlug: string) => {
  return `/app/${projectSlug}/invocations`;
};

export const buildHubPath = (workspaceSlug: string) => {
  return `/build-hub/${workspaceSlug}`;
};

export const buildHubAccessPath = (workspaceSlug: string) => {
  return `/build-hub/organization/${workspaceSlug}/access`;
};

export const buildHubTrialPath = (workspaceSlug: string) => {
  return `/build-hub/organization/${workspaceSlug}/trial`;
};

export const buildHubCardExemptionPath = (workspaceSlug: string) => {
  return `/workspaces/${workspaceSlug}/build_hub_card_exemption`;
};

export const buildHubComputeResourceOverviewPath = (workspaceSlug: string) => {
  return `/build-hub/organization/${workspaceSlug}/compute_resource_overview`;
};

export const buildHubGitHubAppConnectPath = (workspaceSlug: string, machinePoolId?: string) => {
  const base = `/build-hub/${workspaceSlug}/integrations/github-app/connect`;
  if (machinePoolId) {
    return `${base}?machine_pool_id=${encodeURIComponent(machinePoolId)}`;
  }
  return base;
};

export const buildHubMachinePoolConfigurationPath = (workspaceSlug: string, machinePoolId?: string) => {
  let path = `/build-hub/${workspaceSlug}/machine-pools/${machinePoolId ? `${machinePoolId}/edit` : 'new'}`;

  return path;
};

export const devEnvironmentsPath = (workspaceSlug: string) => {
  return `/dev-environments/${workspaceSlug}`;
};

export const devEnvironmentsWelcomePath = (workspaceSlug: string) => {
  return `${devEnvironmentsPath(workspaceSlug)}#/welcome`;
};
